// src/lib/team/signoff-grid.ts
import { formatHours, type TeamMember } from "@/lib/team/metrics";
import { TIME_ZONE } from "@/lib/time/constants";
import { addDays, type IsoDate, monthStartOf, weekEndOf } from "@/lib/time/dates";
import { isLateNow, missingHours, type SignOffLike } from "@/lib/time/signoff-rules";

/**
 * My Team "Time sign-offs" (DECISIONS 2026-10-15): a person x period status grid replacing
 * Vinyl's 48-row list. Lateness is Time Entry's rule, never re-derived here.
 */

export interface TeamSignOffRow extends SignOffLike {
  personId: string;
  personName: string;
  periodId: string;
  signedOffOn: string | null;
  reasonName: string | null;
  comment: string | null;
}

export type SignOffCellState = "onTime" | "late" | "overdue" | "open";

export const SIGNOFF_STATE_LABELS: Record<SignOffCellState, string> = {
  onTime: "On time",
  late: "Late",
  overdue: "Open, overdue",
  open: "Open",
};

export interface SignOffColumn {
  key: string;
  label: string;
  periodType: "week" | "month";
  start: IsoDate;
  end: IsoDate;
}

interface SignOffCell {
  state: SignOffCellState;
  text: string;
  title: string;
}

interface SignOffGridRow {
  personId: string;
  personName: string;
  cells: (SignOffCell | null)[];
}

export interface SignOffGrid {
  columns: SignOffColumn[];
  rows: SignOffGridRow[];
  overdueCount: number;
}

const WEEK_COLUMNS = 4;
const utc = (d: IsoDate) => new Date(`${d}T00:00:00Z`);
const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});
const MONTH_NAME = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "long" });
const SIGNED_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: TIME_ZONE,
  month: "short",
  day: "numeric",
});
const SIGNED_TIME = new Intl.DateTimeFormat("en-US", {
  timeZone: TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
});

const columnKey = (periodType: "week" | "month", start: IsoDate) => `${periodType}:${start}`;

export function signOffColumns(today: IsoDate): SignOffColumn[] {
  const currentStart = addDays(weekEndOf(today), -6);
  const weeks: SignOffColumn[] = Array.from({ length: WEEK_COLUMNS }, (_, i) => {
    const start = addDays(currentStart, -7 * (WEEK_COLUMNS - 1 - i));
    return {
      key: columnKey("week", start),
      label: MONTH_DAY.format(utc(start)),
      periodType: "week",
      start,
      end: addDays(start, 6),
    };
  });
  const monthEnd = addDays(monthStartOf(today), -1);
  const monthStart = monthStartOf(monthEnd);
  const month: SignOffColumn = {
    key: columnKey("month", monthStart),
    label: MONTH_NAME.format(utc(monthStart)),
    periodType: "month",
    start: monthStart,
    end: monthEnd,
  };
  return [...weeks, month].sort(
    (a, b) =>
      a.end.localeCompare(b.end) ||
      (a.periodType === b.periodType ? 0 : a.periodType === "week" ? -1 : 1),
  );
}

/** Period end dates to ask `ed-team-signoffs` for. */
export function signOffWindow(today: IsoDate): { from: IsoDate; to: IsoDate } {
  const ends = signOffColumns(today)
    .map((c) => c.end)
    .sort();
  return { from: ends[0] ?? today, to: ends.at(-1) ?? today };
}

export function cellState(row: TeamSignOffRow, now: Date): SignOffCellState {
  if (row.status === "SignedOff") return row.isLate ? "late" : "onTime";
  return isLateNow(row, now) ? "overdue" : "open";
}

function formatSignedAt(iso: string): string {
  const at = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  // Two formatters: a single dateStyle/timeStyle string joins with "at" or "," by ICU version.
  return `${SIGNED_DAY.format(at)}, ${SIGNED_TIME.format(at)}`;
}

function toCell(row: TeamSignOffRow, column: SignOffColumn, now: Date): SignOffCell {
  const state = cellState(row, now);
  const missing = missingHours(row);
  const signed = row.status === "SignedOff";
  const text = signed
    ? formatHours(row.totalHours)
    : missing > 0
      ? `${formatHours(missing)} missing`
      : "Open";
  const hours = `${formatHours(row.totalHours)} hrs${missing > 0 ? `, ${formatHours(missing)} missing` : ""}`;
  const title = [
    row.personName,
    column.label,
    SIGNOFF_STATE_LABELS[state],
    hours,
    row.reasonName,
    row.comment,
    row.signedOffOn ? `Signed off ${formatSignedAt(row.signedOffOn)}` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
  return { state, text, title };
}

export function buildSignOffGrid(
  members: TeamMember[],
  rows: TeamSignOffRow[],
  today: IsoDate,
  now: Date,
): SignOffGrid {
  const columns = signOffColumns(today);
  const byKey = new Map(rows.map((r) => [`${r.personId}|${columnKey(r.periodType, r.start)}`, r]));
  let overdueCount = 0;
  const gridRows = [...members]
    .sort((a, b) => a.personName.localeCompare(b.personName))
    .map((m) => ({
      personId: m.personId,
      personName: m.personName,
      cells: columns.map((c) => {
        const found = byKey.get(`${m.personId}|${c.key}`);
        if (!found) return null;
        const cell = toCell(found, c, now);
        if (cell.state === "overdue") overdueCount += 1;
        return cell;
      }),
    }));
  return { columns, rows: gridRows, overdueCount };
}
